/**
 * Bridges the app-wide DateDropdown period vocabulary onto the admin
 * signup-funnel endpoint.
 *
 * The endpoint natively understands a small set of presets; anything else
 * (30d/90d/12m and custom `YYYY-MM-DD:YYYY-MM-DD` ranges) is expressed as
 * explicit startDate/endDate, matching how admin/ads-signup-attribution already
 * accepts dates. The nearest preset always rides along so the request still
 * resolves to a sane window on backends that ignore the dates.
 * See docs/TEMP_ADMIN_SIGNUP_FUNNEL_DATE_RANGE.md.
 */
import {
  differenceInCalendarDays,
  endOfDay,
  format,
  parse,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns'

export type AdminFunnelWindow = {
  /** Preset the API understands — also the fallback when dates are ignored. */
  preset: string
  /** `YYYY-MM-DD`, only set when the period cannot be expressed as a preset. */
  startDate?: string
  /** `YYYY-MM-DD`, only set when the period cannot be expressed as a preset. */
  endDate?: string
  /** Resolved window start, for proration and per-day bucketing. */
  from: Date
  /** Resolved window end, for proration and per-day bucketing. */
  to: Date
  /** Inclusive day count of the window (minimum 1). */
  days: number
}

/** Periods the endpoint already supports natively — no dates needed. */
const NATIVE_PRESETS: Record<string, string> = {
  '1d': '24h',
  '7d': '7d',
  mtd: 'mtd',
  ytd: 'ytd',
}

const ISO = 'yyyy-MM-dd'

/** Parse a `YYYY-MM-DD` string without the UTC drift of `new Date(str)`. */
function parseISODate(value: string): Date | null {
  const parsed = parse(value, ISO, new Date())
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Coarse preset that best covers `days`, used as the ignore-the-dates fallback. */
function nearestPreset(days: number): string {
  if (days <= 1) return '24h'
  if (days <= 7) return '7d'
  if (days <= 31) return 'mtd'
  if (days <= 92) return 'qtd'
  return 'ytd'
}

function windowFor(from: Date, to: Date): { from: Date; to: Date; days: number } {
  return {
    from: startOfDay(from),
    to: endOfDay(to),
    days: Math.max(1, differenceInCalendarDays(to, from) + 1),
  }
}

/** Resolve a DateDropdown period into the request params + concrete window. */
export function resolveAdminFunnelWindow(period: string): AdminFunnelWindow {
  const now = new Date()

  if (period.includes(':')) {
    const [startStr, endStr] = period.split(':')
    const start = parseISODate(startStr)
    const end = parseISODate(endStr)
    if (start && end) {
      const range = windowFor(start, end)
      return {
        preset: nearestPreset(range.days),
        startDate: format(range.from, ISO),
        endDate: format(range.to, ISO),
        ...range,
      }
    }
  }

  const native = NATIVE_PRESETS[period]
  if (native) {
    const from =
      period === 'mtd'
        ? startOfMonth(now)
        : period === 'ytd'
          ? startOfYear(now)
          : subDays(now, period === '1d' ? 0 : 6)
    return { preset: native, ...windowFor(from, now) }
  }

  const from =
    period === '90d'
      ? subDays(now, 89)
      : period === '12m'
        ? subMonths(now, 12)
        : subDays(now, 29)
  const range = windowFor(from, now)
  return {
    preset: nearestPreset(range.days),
    startDate: format(range.from, ISO),
    endDate: format(range.to, ISO),
    ...range,
  }
}
